/**
 * Locale-aware formatting for all customer-facing dates, numbers, and prices.
 *
 * Every component must format through this module — never `toFixed`,
 * `toLocaleDateString('en-US')`, or string-built prices. French follows
 * OQLF/fr-CA conventions:
 *   - decimal comma and non-breaking-space thousands separator (12 345,67)
 *   - amount before the symbol with a non-breaking space ("59,99 $")
 */
import { getLanguage, type Language } from '../config/siteMode';

const LOCALES: Record<Language, string> = {
  en: 'en-CA',
  fr: 'fr-CA',
};

const locale = (lang?: Language): string => LOCALES[lang ?? getLanguage()];

const toValidDate = (value: string | Date): Date | null => {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** Full date, e.g. "June 11, 2026" / "11 juin 2026". */
export const formatDate = (value: string | Date, lang?: Language): string => {
  const date = toValidDate(value);
  if (!date) return '';
  return new Intl.DateTimeFormat(locale(lang), {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
};

/** Compact month + year, e.g. "Jun 2026" / "juin 2026" (badges). */
export const formatMonthYear = (value: string | Date, lang?: Language): string => {
  const date = toValidDate(value);
  if (!date) return '';
  return new Intl.DateTimeFormat(locale(lang), {
    month: 'short',
    year: 'numeric',
  }).format(date);
};

/** Plain number with locale separators, e.g. "12,345" / "12 345". */
export const formatNumber = (value: number, lang?: Language): string =>
  new Intl.NumberFormat(locale(lang)).format(value);

/** Currency in CAD: "$59.99" (en) / "59,99 $" (fr, non-breaking space). */
export const formatPrice = (amount: number, lang?: Language): string =>
  new Intl.NumberFormat(locale(lang), {
    style: 'currency',
    currency: 'CAD',
    currencyDisplay: 'narrowSymbol',
  }).format(amount);

/** Year only, e.g. "2026". */
export const formatYear = (value: string | Date, lang?: Language): string => {
  const date = toValidDate(value);
  if (!date) return '';
  return new Intl.DateTimeFormat(locale(lang), { year: 'numeric' }).format(date);
};
